import { useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type {
  EarlyAccessIn,
  PublicationAudioOut,
  PublicationDetailOut,
  PublicationEbookOut,
} from '@/api/generated/schemas';
import { orDash } from '@/components/common/show/shared';
import { extractBackendMessage } from '@/utils/apiError';
import { formatDate } from '@/utils/format';
import { toPickerValue } from '@/pages/user_catalog/publications/show/formatExclusiveUntil';
import type { EarlyAccessState } from './earlyAccess';
import { earlyAccessLeadDays, earlyAccessState } from './earlyAccess';

const { Text, Title, Paragraph } = Typography;

const T = 'user_catalog.publications.show.early_access';

const GUIDE_SECTIONS = ['what', 'why', 'effects'] as const;
const GUIDE_LIMITS = [
  'needs_premiere',
  'before_premiere',
  'import',
  'availability',
  'no_follow',
  'label',
] as const;

/** A short user guide: what early access is, why, effects and limits. */
const EarlyAccessGuide = ({ onClose }: { onClose: () => void }) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const G = `${T}.guide`;

  return (
    <Modal
      open
      onCancel={onClose}
      title={t(`${G}.title`)}
      footer={
        <Button type="primary" onClick={onClose}>
          {t(`${G}.close`)}
        </Button>
      }
    >
      {/* The one thing to take away before reading the rest. */}
      <Alert
        type="warning"
        showIcon
        message={t(`${G}.warning_title`)}
        description={t(`${G}.warning`)}
        style={{ marginBottom: token.margin }}
      />
      {GUIDE_SECTIONS.map((key) => (
        <div key={key}>
          <Title level={5}>{t(`${G}.${key}_title`)}</Title>
          <Paragraph>{t(`${G}.${key}`)}</Paragraph>
        </div>
      ))}
      <Title level={5}>{t(`${G}.limits_title`)}</Title>
      <Paragraph>
        <ul>
          {GUIDE_LIMITS.map((key) => (
            <li key={key}>{t(`${G}.limit_${key}`)}</li>
          ))}
        </ul>
      </Paragraph>
    </Modal>
  );
};

type ProductKey = 'ebook' | 'audio';

interface ProductRow {
  kind: ProductKey;
  product: PublicationEbookOut | PublicationAudioOut;
}

const stateColors: Record<EarlyAccessState, string | undefined> = {
  scheduled: 'blue',
  active: 'green',
  ended: undefined,
  ineffective: 'orange',
};

interface EarlyAccessModalProps {
  label: string;
  product: ProductRow['product'];
  pending: boolean;
  onSave: (value: Dayjs, onError: (error: unknown) => void) => void;
  onClose: () => void;
}

/** Mounted only while open, so every opening starts from the stored date. */
const EarlyAccessModal = ({
  label,
  product,
  pending,
  onSave,
  onClose,
}: EarlyAccessModalProps) => {
  const t = useTranslate();
  const [value, setValue] = useState(toPickerValue(product.accessible_from));
  const [saveError, setSaveError] = useState<string | null>(null);
  // Asked on every save, a reschedule included: each one decides when every
  // user gets the files.
  const [confirmed, setConfirmed] = useState(false);

  const save = () => {
    if (!value || !confirmed) return;
    setSaveError(null);
    onSave(value, (error) =>
      setSaveError(extractBackendMessage(error) ?? t(`${T}.saved_failed`))
    );
  };

  return (
    <Modal
      open
      onCancel={onClose}
      title={t(`${T}.modal_title`, { format: label })}
      okText={t('actions.save')}
      cancelText={t('actions.cancel')}
      onOk={save}
      okButtonProps={{ disabled: !value || !confirmed, loading: pending }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">
          {t(`${T}.hint`, { premiere: formatDate(product.published_at) })}
        </Text>
        <DatePicker
          value={value}
          onChange={setValue}
          format="DD.MM.YYYY"
          placeholder={t(`${T}.placeholder`)}
          disabled={pending}
          style={{ width: '100%' }}
        />
        {/* Warned, not blocked: titles whose premiere was rewritten by hand
            need their date entered before a re-import restores the real one. */}
        {value &&
          earlyAccessState({
            published_at: product.published_at,
            accessible_from: value.toISOString(),
          }) === 'ineffective' && (
            <Alert
              type="warning"
              showIcon
              message={t(`${T}.ineffective_warning`)}
            />
          )}
        <Checkbox
          checked={confirmed}
          disabled={pending}
          onChange={(event) => setConfirmed(event.target.checked)}
        >
          {t(`${T}.publisher_confirm`)}
        </Checkbox>
        {saveError && <Alert type="error" showIcon message={saveError} />}
      </Space>
    </Modal>
  );
};

interface EarlyAccessCardProps {
  record: PublicationDetailOut;
}

/**
 * Per-product early access. Each product has its own endpoint, so actions sit
 * on the row rather than in one modal for both, as exclusivity does.
 */
export const EarlyAccessCard = ({ record }: EarlyAccessCardProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation<PublicationDetailOut>();
  const [editing, setEditing] = useState<ProductRow | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const { token } = theme.useToken();

  const label = (kind: ProductKey) =>
    t(`user_catalog.publications.tabs.${kind}`);

  const rows: ProductRow[] = (['ebook', 'audio'] as const).flatMap((kind) => {
    const product = record[kind];
    return product ? [{ kind, product }] : [];
  });

  const submit = (
    kind: ProductKey,
    value: Dayjs | null,
    onError: (error: unknown) => void
  ) => {
    const outcome = value ? 'saved' : 'cancelled';
    mutate(
      {
        url: `user_catalog/publications/${record.id}/products/${kind}/early_access`,
        method: value ? 'put' : 'delete',
        values: value
          ? ({
              accessible_from: value.startOf('day').toISOString(),
              // The statement exactly as the moderator saw and ticked it,
              // for the audit record.
              publisher_confirmation: t(`${T}.publisher_confirm`),
            } satisfies EarlyAccessIn)
          : {},
        successNotification: {
          type: 'success',
          message: t('notifications.success'),
          description: t(`${T}.${outcome}`),
        },
        errorNotification: false,
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'user_catalog.publications',
            id: record.id,
            invalidates: ['detail'],
          });
          setEditing(null);
        },
        onError,
      }
    );
  };

  const cancel = (kind: ProductKey) =>
    submit(kind, null, (error) =>
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description: extractBackendMessage(error) ?? t(`${T}.cancelled_failed`),
      })
    );

  const stateTag = ({ product }: ProductRow) => {
    const state = earlyAccessState(product);
    if (!state) return orDash(null);
    const lead =
      (state === 'scheduled' || state === 'active') &&
      product.accessible_from &&
      product.published_at
        ? earlyAccessLeadDays(product.accessible_from, product.published_at)
        : null;
    const tag = (
      <Tag color={stateColors[state]}>
        {t(`${T}.state_${state}`)}
        {lead != null && ` · ${t(`${T}.lead_days`, { count: lead })}`}
      </Tag>
    );
    return state === 'ineffective' ? (
      <Tooltip title={t(`${T}.ineffective_hint`)}>{tag}</Tooltip>
    ) : (
      tag
    );
  };

  return (
    <Card
      title={
        <Space size={4}>
          {t('user_catalog.publications.show.sections.early_access')}
          <Tooltip title={t(`${T}.guide.open`)}>
            <Button
              type="text"
              size="small"
              aria-label={t(`${T}.guide.open`)}
              icon={
                <InfoCircleOutlined style={{ color: token.colorPrimary }} />
              }
              onClick={() => setGuideOpen(true)}
            />
          </Tooltip>
        </Space>
      }
    >
      <Table<ProductRow>
        dataSource={rows}
        rowKey="kind"
        pagination={false}
        size="small"
        locale={{ emptyText: orDash(null) }}
        columns={[
          {
            title: t('user_catalog.publications.show.windows.format'),
            key: 'format',
            render: (_, row) => label(row.kind),
          },
          {
            title: t('user_catalog.publications.show.windows.published_at'),
            key: 'published_at',
            render: (_, row) => orDash(formatDate(row.product.published_at)),
          },
          {
            title: t(`${T}.column`),
            key: 'accessible_from',
            render: (_, row) => orDash(formatDate(row.product.accessible_from)),
          },
          {
            title: t('user_catalog.publications.show.windows.state'),
            key: 'state',
            render: (_, row) => stateTag(row),
          },
          {
            key: 'actions',
            align: 'right',
            render: (_, row) => {
              const blocked = !row.product.published_at;
              return (
                <Space size={0}>
                  <Tooltip title={blocked ? t(`${T}.no_premiere`) : undefined}>
                    {/* A disabled button swallows hover, so the tooltip
                        needs a wrapper of its own to stay reachable. */}
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        type="link"
                        size="small"
                        disabled={blocked}
                        onClick={() => setEditing(row)}
                      >
                        {t(
                          row.product.accessible_from ? `${T}.edit` : `${T}.set`
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                  {row.product.accessible_from && (
                    <Popconfirm
                      title={t(`${T}.cancel_confirm_title`)}
                      description={t(`${T}.cancel_confirm_description`)}
                      okText={t(`${T}.cancel`)}
                      okButtonProps={{
                        danger: true,
                        loading: mutation.isPending,
                      }}
                      cancelText={t('actions.cancel')}
                      onConfirm={() => cancel(row.kind)}
                    >
                      <Button type="link" size="small" danger>
                        {t(`${T}.cancel`)}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              );
            },
          },
        ]}
      />

      {editing && (
        <EarlyAccessModal
          label={label(editing.kind)}
          product={editing.product}
          pending={mutation.isPending}
          onSave={(value, onError) => submit(editing.kind, value, onError)}
          onClose={() => setEditing(null)}
        />
      )}
      {guideOpen && <EarlyAccessGuide onClose={() => setGuideOpen(false)} />}
    </Card>
  );
};
